import Container from '@material-ui/core/Container';
import Grid from '@material-ui/core/Grid';
import Link from '@material-ui/core/Link';
import { makeStyles } from '@material-ui/core/styles';
import Typography from '@material-ui/core/Typography';
import clsx from 'clsx';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Controller, Scene } from 'react-scrollmagic';

import arpImg from '../../images/editor/Augmented-Reality-Platform.jpg';
import targetImg from '../../images/editor/target-images.jpg';
import { EDITOR_HOST } from '../../links';
import ColumnItem from './column-item';
import ModelIcon from './icons/3d-model';
import ActionButtonsIcon from './icons/actionButtons';
import AudioIcon from './icons/audio';
import FactsIcon from './icons/facts';
import ImagesIcon from './icons/images';
import TextIcon from './icons/text';
import VideoIcon from './icons/video';

const useStyles = makeStyles(theme => ({
  root: {
    padding: theme.spacing(10, 0)
  },
  animate: {
    position: 'relative',
    top: 20,
    transition:
      'opacity 0.5s cubic-bezier(0.455, 0.03, 0.515, 0.955), top 0.5s cubic-bezier(0.455, 0.03, 0.515, 0.955)',
    opacity: 0
  },
  show: {
    top: 0,
    opacity: 1
  },
  descr: {
    marginBottom: theme.spacing(6),
    transitionDelay: '0.1s'
  },
  section: {
    marginBottom: theme.spacing(8),
    '&:last-of-type': {
      marginBottom: 0
    }
  },
  titleSection: {
    marginBottom: theme.spacing(6),
    '& svg': {
      marginRight: theme.spacing(1)
    }
  },
  imgSection: {
    display: 'block',
    width: '100%',
    height: 'auto',
    marginBottom: theme.spacing(6),
    borderRadius: theme.shape.borderRadius
  },
  d0: {
    transitionDelay: '0.3s'
  },
  d1: {
    transitionDelay: '0.6s'
  },
  d2: {
    transitionDelay: '0.9s'
  },
  d3: {
    transitionDelay: '1.2s'
  },
  d4: {
    transitionDelay: '1.5s'
  },
  d5: {
    transitionDelay: '1.8s'
  },
  d6: {
    transitionDelay: '2.1s'
  },
  d7: {
    transitionDelay: '2.2s'
  },
  d8: {
    transitionDelay: '2.5s'
  },
  d9: {
    transitionDelay: '2.8s'
  },
  d10: {
    transitionDelay: '3.3s'
  }
}));

const data = {
  targetImg: {
    blockOne: {
      firstText: ['editorPage.contentBlock.targetImg.block1.descr'],
      list: [
        {
          highlightText:
            'editorPage.contentBlock.targetImg.block1.list.highlightText1',
          text: 'editorPage.contentBlock.targetImg.block1.list.descr1'
        },
        {
          highlightText:
            'editorPage.contentBlock.targetImg.block1.list.highlightText2',
          text: 'editorPage.contentBlock.targetImg.block1.list.descr2'
        }
      ]
    },
    blockTwo: {
      firstText: ['editorPage.contentBlock.targetImg.block2.descr'],
      list: [
        {
          text: 'editorPage.contentBlock.targetImg.block2.list.descr1'
        },
        {
          text: 'editorPage.contentBlock.targetImg.block2.list.descr2'
        },
        {
          text: 'editorPage.contentBlock.targetImg.block2.list.descr3'
        },
        {
          text: 'editorPage.contentBlock.targetImg.block2.list.descr4'
        }
      ],
      lastText: ['editorPage.contentBlock.targetImg.block2.text']
    }
  },
  models: {
    firstText: [
      'editorPage.contentBlock.models.descr1',
      'editorPage.contentBlock.models.descr2'
    ],
    list: [
      {
        highlightText: 'editorPage.contentBlock.models.list.highlightText1',
        text: 'editorPage.contentBlock.models.list.descr1'
      },
      {
        highlightText: 'editorPage.contentBlock.models.list.highlightText2',
        text: 'editorPage.contentBlock.models.list.descr2'
      },
      {
        highlightText: 'editorPage.contentBlock.models.list.highlightText3',
        text: 'editorPage.contentBlock.models.list.descr3'
      }
    ],
    lastText: ['editorPage.contentBlock.models.descr3']
  },
  video: {
    firstText: [
      'editorPage.contentBlock.video.descr1',
      'editorPage.contentBlock.video.descr2',
      'editorPage.contentBlock.video.descr3'
    ]
  },
  facts: {
    firstText: ['editorPage.contentBlock.facts.descr']
  },
  actionButtons: {
    firstText: ['editorPage.contentBlock.actionBtns.descr']
  },
  images: {
    firstText: ['editorPage.contentBlock.images.descr']
  },
  text: {
    firstText: ['editorPage.contentBlock.text.descr']
  },
  audio: {
    firstText: ['editorPage.contentBlock.audio.descr']
  }
};

const ContentBlock = () => {
  const classes = useStyles();
  const { t } = useTranslation();

  return (
    <div className={classes.root}>
      <Container maxWidth="xl">
        <Controller>
          <Scene
            classToggle={[`.${classes.animate}`, `${classes.show}`]}
            triggerElement="#editorContent"
            indicators={false}
            reverse={false}
            offset="-200px"
          >
            <div id="editorContent">
              <Typography
                className={clsx(classes.descr, classes.animate)}
                variant="body1"
              >
                {t('editorPage.contentBlock.descr')}
              </Typography>

              {/* Target image */}
              <div className={classes.section}>
                <Typography
                  className={clsx(
                    classes.titleSection,
                    classes.d0,
                    classes.animate
                  )}
                  variant="h3"
                  color="primary"
                >
                  {t('editorPage.contentBlock.targetImg.title')}
                </Typography>
                <img
                  className={clsx(
                    classes.imgSection,
                    classes.d1,
                    classes.animate
                  )}
                  src={targetImg}
                  alt={t('editorPage.contentBlock.targetImg.title')}
                />
                <Grid container spacing={8}>
                  <Grid
                    item
                    xs={12}
                    md={6}
                    className={clsx(classes.d2, classes.animate)}
                  >
                    <ColumnItem data={data.targetImg.blockOne} />
                  </Grid>
                  <Grid
                    item
                    xs={12}
                    md={6}
                    className={clsx(classes.d3, classes.animate)}
                  >
                    <ColumnItem data={data.targetImg.blockTwo} />
                  </Grid>
                </Grid>
              </div>

              {/* 3D models & Video */}

              <div className={classes.section}>
                <Grid container spacing={8}>
                  <Grid
                    item
                    xs={12}
                    md={6}
                    className={clsx(classes.d3, classes.animate)}
                  >
                    <Typography
                      className={classes.titleSection}
                      variant="h3"
                      color="primary"
                    >
                      <ModelIcon /> {t('editorPage.contentBlock.models.title')}
                    </Typography>
                    <ColumnItem data={data.models} />
                  </Grid>
                  <Grid
                    item
                    xs={12}
                    md={6}
                    className={clsx(classes.d4, classes.animate)}
                  >
                    <Typography
                      className={classes.titleSection}
                      variant="h3"
                      color="primary"
                    >
                      <VideoIcon /> {t('editorPage.contentBlock.video.title')}
                    </Typography>
                    <ColumnItem data={data.video} />
                  </Grid>
                </Grid>
              </div>

              {/* Facts & Action buttons */}
              <div className={classes.section}>
                <Grid container spacing={8}>
                  <Grid
                    item
                    xs={12}
                    md={6}
                    className={clsx(classes.d5, classes.animate)}
                  >
                    <Typography
                      className={classes.titleSection}
                      variant="h3"
                      color="primary"
                    >
                      <FactsIcon /> {t('editorPage.contentBlock.facts.title')}
                    </Typography>
                    <ColumnItem data={data.facts} />
                  </Grid>
                  <Grid
                    item
                    xs={12}
                    md={6}
                    className={clsx(classes.d6, classes.animate)}
                  >
                    <Typography
                      className={classes.titleSection}
                      variant="h3"
                      color="primary"
                    >
                      <ActionButtonsIcon />{' '}
                      {t('editorPage.contentBlock.actionBtns.title')}
                    </Typography>
                    <ColumnItem data={data.actionButtons} />
                  </Grid>
                </Grid>
              </div>

              {/* Images, text, audio */}
              <div className={classes.section}>
                <Grid container spacing={8}>
                  <Grid
                    item
                    xs={12}
                    md={4}
                    className={clsx(classes.d7, classes.animate)}
                  >
                    <Typography
                      className={classes.titleSection}
                      variant="h3"
                      color="primary"
                    >
                      <ImagesIcon /> {t('editorPage.contentBlock.images.title')}
                    </Typography>
                    <ColumnItem data={data.images} />
                  </Grid>
                  <Grid
                    item
                    xs={12}
                    md={4}
                    className={clsx(classes.d8, classes.animate)}
                  >
                    <Typography
                      className={classes.titleSection}
                      variant="h3"
                      color="primary"
                    >
                      <TextIcon /> {t('editorPage.contentBlock.text.title')}
                    </Typography>
                    <ColumnItem data={data.text} />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Typography
                      className={classes.titleSection}
                      variant="h3"
                      color="primary"
                    >
                      <AudioIcon /> {t('editorPage.contentBlock.audio.title')}
                    </Typography>
                    <ColumnItem data={data.audio} />
                  </Grid>
                </Grid>
              </div>

              {/* AR builder */}
              <div
                className={clsx(classes.section, classes.d9, classes.animate)}
              >
                <Typography
                  className={classes.titleSection}
                  variant="h3"
                  color="primary"
                >
                  {t('editorPage.contentBlock.builder.title')}
                </Typography>
                <Typography className={classes.descr} variant="body1">
                  {t('editorPage.contentBlock.builder.text1')}
                </Typography>
                <img
                  className={classes.imgSection}
                  src={arpImg}
                  alt={t('editorPage.contentBlock.builder.title')}
                />
                <Typography className={classes.descr} variant="body1">
                  {t('editorPage.contentBlock.builder.text2')}
                </Typography>
                <Typography className={classes.descr} variant="body1">
                  <Link href={EDITOR_HOST}>
                    {t('editorPage.contentBlock.builder.link')}
                  </Link>{' '}
                  {t('editorPage.contentBlock.builder.text3')}
                </Typography>
              </div>

              {/* WebAR */}
              <div
                className={clsx(classes.section, classes.d10, classes.animate)}
              >
                <Typography
                  className={classes.titleSection}
                  variant="h3"
                  color="primary"
                >
                  {t('editorPage.contentBlock.webAR.title')}
                </Typography>
                <Typography className={classes.descr} variant="body1">
                  {t('editorPage.contentBlock.webAR.text')}
                </Typography>
                <Typography className={classes.descr} variant="body1">
                  {t('editorPage.contentBlock.webAR.linkText1')}{' '}
                  <Link href={EDITOR_HOST}>
                    {t('editorPage.contentBlock.webAR.link')}
                  </Link>{' '}
                  {t('editorPage.contentBlock.webAR.linkText2')}
                </Typography>
              </div>
            </div>
          </Scene>
        </Controller>
      </Container>
    </div>
  );
};

export default ContentBlock;
